import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Divider, Menu, MenuItem, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { useConfirm } from "material-ui-confirm";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { hasPermission } from "../../../utils/helpers";
import { StoreType } from "../../../utils/types";
import {
    fetchWorkerJobAuditLog,
	setIsJobDetailsDialogOpen,
	setSelectedWorkerAndJob,
} from "../store/jobs-matrix-slice";
import { JobStatus } from "../store/types";

interface Params {
    row: {
        jobs: { id: number; jobStatusCode: string; isExpired: boolean }[];
        [key: string]: any;
    };
}

interface ColumnJobCellProps {
    params: Params;
    jobId: number;
    handleJobStatusChange: (
        params: Params,
        jobId: number,
        statusCode: string,
        setState: React.Dispatch<React.SetStateAction<string | null>>
    ) => void;
    jobStatuses: JobStatus[];
}



const getStatusBox = (statusCode: string | null | undefined, isOpen: boolean) => {
    const renderContent = () => {
        switch (statusCode) {
            case "QUALIFIED":
                return <CheckOutlinedIcon sx={{ fontSize: 20, color: "#388E3C" }} />;
            case "EXPIRED":
                return <AssignmentLateOutlinedIcon sx={{ fontSize: 20, color: "#E44B48" }} />;
            case "NOT_QUALIFIED":
                return <>-</>;
            default:
                return null;
        }
    };

    const getBackgroundColor = () => {
        switch (statusCode) {
            case "QUALIFIED":
                return "#F0FFF7";
            case "EXPIRED":
                return "#FFF9F9";
            default:
                return "transparent";
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: getBackgroundColor(),
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: isOpen ? "1px solid #2F4D8B" : "2px solid transparent",
                boxSizing: "border-box",
                position: "relative",
            }}
        >
            {renderContent()}
            {isOpen && (
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        color: "#000",
                        position: "absolute",
                        right: "50%",
                        marginRight: "-30px",
                        transform: "translateX(16px)",
                    }}
                />
            )}
        </Box>
    );
};

const getStatusLabel = (statusCode: string, t: TFunction): string => {
    switch (statusCode) {
        case "QUALIFIED":
            return t("jobs-matrix_qualified");
        case "EXPIRED":
            return t("jobs-matrix_expired");
        case "NOT_QUALIFIED":
            return t("jobs-matrix_not-qualified");
        default:
            return "--";
    }
};

const getMenuItemLabel = (statusCode: string, t: TFunction): string => {
    switch (statusCode) {
        case "NOT_QUALIFIED":
            return t("jobs-matrix_remove-job");
        default:
            return getStatusLabel(statusCode, t);
    }
};

const ColumnJobCell: React.FC<ColumnJobCellProps> = ({
    params,
    jobId,
    handleJobStatusChange,
    jobStatuses,
}) => {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const dispatch = useDispatch();

    const workerJob = params.row.jobs?.find(
        (x) => parseInt(String(x.id)) === parseInt(String(jobId))
    );

    const [state, setState] = useState<string | null>(
        workerJob?.jobStatusCode || "NOT_QUALIFIED"
    );
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const userRolesWithPermission = useSelector(
        (state: any) => state.user.userData.userRolesWithPermissions
    );
    const { userRoles, userWorkCenters, allJobs } = useSelector((state: StoreType) => ({
        userRoles: state.user.userData.userRoles,
        // Jobs Matrix fetches its own default filters on mount, so this works
        // even when Cross Skilling is disabled and the Skills Matrix tab
        // (the only writer of manageWorkers.skillMatrixAppliedFilters) never mounts.
        userWorkCenters:
            state.jobsMatrix?.defaultFiltersData?.workcenters?.split(",") || [],
        allJobs: state.jobsMatrix?.filterData?.allJobRoles || [],
    }));

    const isReadOnlyUser = useMemo(() => {
        return userRoles.includes("ReadOnly");
    }, [userRoles]);

    const isInUserWorkcenterAndHasPermission = useMemo(() => {
        const workerWorkCenters = params.row.workCenterIds?.split(",") || [];
        return workerWorkCenters.some((wc: string) => userWorkCenters.includes(wc));
    }, [params.row.workCenterIds, userWorkCenters]);

    const handleisInUserWorkcenterAlert = useCallback(() => {
        confirm({
            title: "",
            description: t("jobs-matrix_no-workcenter-access"),
            confirmationText: t("common_close"),
            cancellationText: "",
        });
    }, [confirm, t]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (
            hasPermission(
                userRolesWithPermission,
                "JOBS_MATRIX.UPDATE_JOB_STATUS"
            )
        ) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const updateJobStatus = useCallback(
        async (statusCode: string) => {
            // Check if the new status is the same as current status
            if (statusCode === state) {
                handleClose();
                return;
            }

            // Show confirmation dialog when removing job
            if (statusCode === "NOT_QUALIFIED") {
                try {
                    const workerName = `${params.row.firstName} ${params.row.lastName}`;
                    const job = allJobs.find((j: any) => j.id === jobId);
                    const jobName = job?.name || t("jobs-matrix_this-job");

                    await confirm({
                        title: t("jobs-matrix_remove-job"),
                        description: t("jobs-matrix_remove-job-confirm", {
                            jobName,
                            workerName,
                        }),
                        confirmationText: t("jobs-matrix_remove"),
                        cancellationText: t("common_cancel"),
                    });
                    handleJobStatusChange(params, jobId, statusCode, setState);
                } catch {
                    // User cancelled the action
                    handleClose();
                }
            } else {
                handleJobStatusChange(params, jobId, statusCode, setState);
            }
        },
        [state, handleClose, confirm, handleJobStatusChange, params, jobId, allJobs, t]
    );

    const handleViewDetails = useCallback(() => {
        dispatch(setSelectedWorkerAndJob({
            worker: params.row,
            job: {
                id: jobId,
            },
        }));
        dispatch(fetchWorkerJobAuditLog(
            {
                workerId: params.row.workerId,
                jobId: jobId,
            }
        ))
        dispatch(setIsJobDetailsDialogOpen(true));
        handleClose();
    }, [dispatch, params.row, jobId, handleClose]);


    return (
        <>
            <Box
                aria-controls={open ? "job-status-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={(event) => {
                    if (!isReadOnlyUser) {
                        if (isInUserWorkcenterAndHasPermission) {
                            handleClick(event);
                        } else {
                            handleisInUserWorkcenterAlert();
                        }
                    }
                }}
                sx={{
                    cursor:
                        !isReadOnlyUser && isInUserWorkcenterAndHasPermission
                            ? "pointer"
                            : "default",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {getStatusBox(state || "NOT_QUALIFIED", open)}
            </Box>
            {open && (
                <Menu
                    id="job-status-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    slotProps={{
                        paper: {
                            "aria-labelledby": "job-status-button",
                        },
                    }}
                >
                    {(
                        [
                            ...jobStatuses.map((status) => {
                                const isQualified = status.code === "QUALIFIED";
                                const isExpired = status.code === "EXPIRED";
                                const isRemoveJob = status.code === "NOT_QUALIFIED";

                                return (
                                    <Box key={status.id}>
                                        <MenuItem
                                            onClick={() => {
                                                updateJobStatus(status.code)
                                                handleClose();
                                            }}
                                        >
                                            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                                {isQualified && (
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#F0FFF7",
                                                            width: 28,
                                                            height: 28,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        <CheckOutlinedIcon sx={{ fontSize: 20, color: "#388E3C" }} />
                                                    </Box>
                                                )}
                                                {isExpired && (
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#FFF9F9",
                                                            width: 28,
                                                            height: 28,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        <AssignmentLateOutlinedIcon sx={{ fontSize: 20, color: "#E44B48" }} />
                                                    </Box>
                                                )}
                                                {isRemoveJob && (
                                                    <Box sx={{ width: 28, height: 28 }} />
                                                )}
                                                <Typography sx={{
                                                  fontFamily: "Roboto",
                                                  fontSize: "16px",
                                                  paddingRight: "32px",
                                                }}>{getMenuItemLabel(status.code, t)}</Typography>
                                            </Box>
                                        </MenuItem>
                                        {isExpired && <Divider />}
                                    </Box>
                                );
                            }),
                            <MenuItem key="view-details" onClick={handleViewDetails}>
                                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                                    <Box sx={{ width: 28, height: 28 }} />
                                    <Typography sx={{
                                      fontFamily: "Roboto",
                                      fontSize: "16px",
                                      paddingRight: "32px",
                                    }}>{t("jobs-matrix_view-details")}</Typography>
                                </Box>
                            </MenuItem>
                        ]
                    )}
                </Menu>
            )}
        </>
    );
};

export default ColumnJobCell;
